// Create group (SPEC §7.3): name, course, main teacher, room, start date, weekly slots, end date, note.

import type { ApiResult } from "../../api/client";
import { createGroup } from "../../api/groups";
import type { Course, GroupSaveResult } from "../../api/types";
import { t } from "../../i18n/i18n";
import { navigate } from "../../router";
import { requireSchool } from "../../session";
import { createButton, createElement } from "../../ui/dom";
import { showToast } from "../../ui/feedback";
import { createInputField, createSelectField, createTextAreaField, type Field } from "../../ui/forms";
import { createSlotsEditor, type SlotsEditor } from "../../ui/slotsEditor";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import { loadReferenceData, type ReferenceData } from "../referenceData";
import { runGroupSave } from "./groupSaveFlow";
import { createRoomField, createTeacherField, fillTeacherOptions } from "./planFields";

export async function renderGroupCreatePage(container: HTMLElement): Promise<void> {
  const page: HTMLElement = startPage(container, t("groups.newGroup"));
  page.append(createLoading());
  const reference: ApiResult<ReferenceData> = await loadReferenceData();
  if (reference.ok === false) {
    showPageProblem(page, reference.problem);
    return;
  }

  page.lastElementChild?.remove();
  page.append(buildForm(reference.data));
}

function buildForm(reference: ReferenceData): HTMLElement {
  const form: HTMLDivElement = createElement("div", "card");
  const name: Field<HTMLInputElement> = createInputField(t("groups.name"), "text", "");
  const course: Field<HTMLSelectElement> = createCourseField(reference.courses);
  const teacher: Field<HTMLSelectElement> = createTeacherField(reference, "", "");
  course.input.addEventListener("change", (): void => {
    fillTeacherOptions(teacher.input, reference, course.input.value, teacher.input.value);
  });
  const room: Field<HTMLSelectElement> = createRoomField(reference, "");
  const startDate: Field<HTMLInputElement> = createInputField(t("groups.startDate"), "date", requireSchool().today);
  const endDate: Field<HTMLInputElement> = createInputField(t("groups.endDate"), "date", "");
  const note: Field<HTMLTextAreaElement> = createTextAreaField(t("app.note"), "");
  const slots: SlotsEditor = createSlotsEditor([], requireSchool().defaultLessonCells);
  const messages: HTMLDivElement = createElement("div");
  const grid: HTMLDivElement = createElement("div", "form-grid");
  grid.append(name.wrapper, course.wrapper, teacher.wrapper, room.wrapper, startDate.wrapper, endDate.wrapper);
  const save: HTMLButtonElement = createButton(t("app.create"), (): Promise<void> => runGroupSave(
    (confirm: boolean) => createGroup({
      name: name.input.value, courseId: course.input.value, teacherId: teacher.input.value,
      classroomId: room.input.value, startDate: startDate.input.value, endDate: endDate.input.value || null,
      note: note.input.value, slots: slots.getSlots(), confirm,
    }), messages, onCreated), "button-primary");
  form.append(grid, slots.element, note.wrapper, messages, save);
  return form;
}

function createCourseField(courses: Course[]): Field<HTMLSelectElement> {
  const options: { value: string; label: string }[] = courses
    .filter((course: Course): boolean => course.archived === false)
    .map((course: Course): { value: string; label: string } => ({ value: course.id, label: course.name }));
  return createSelectField(t("groups.course"), [{ value: "", label: t("app.none") }, ...options], "");
}

function onCreated(result: GroupSaveResult): void {
  showToast(t("groups.created"));
  navigate(`/groups/${result.group.id}`);
}
